import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import LoadingButton from '@mui/lab/LoadingButton'
import DomainAdd from '@mui/icons-material/DomainAdd'
import MultiSelectAutocomplete, {
    OptionType,
} from '@components/MultiSelectAutocomplete'
import DashboardLoadingDataView from '@pages/Dashboard/components/DashboardLoadingDataView'
import ValidationErrors from './ValidationErrors'
import useCreateOrgCollectionPage from '@pages/Dashboard/actions/CreateOrgCollectionPage/useCreateOrgCollectionPage'

interface OrgCollectionFormProps {
    title: string
    values: {
        name: string
        orgIds: OptionType[]
        orgAdminIds: OptionType[]
        orgCollectionAdminIds: OptionType[]
    }
    setValue: ReturnType<typeof useCreateOrgCollectionPage>['setValue']
    formValid: boolean
    handleSubmit: () => void
    loading: boolean
    error: string | null
    admins: OptionType[]
    orgs: OptionType[]
    validationErrors: string[] | null
}

const OrgCollectionForm: React.FC<OrgCollectionFormProps> = ({
    title,
    values,
    setValue,
    formValid,
    handleSubmit,
    loading,
    error,
    admins,
    orgs,
    validationErrors,
}) => {
    if (loading) {
        return <DashboardLoadingDataView loading={loading} />
    }

    if (error && !validationErrors) {
        return <div>Error in request: {error}</div>
    }

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 64,
            }}
        >
            <Paper
                style={{
                    paddingInline: 32,
                    paddingBlock: 32,
                    minWidth: '50%',
                    maxWidth: '560px',
                    textAlign: 'center',
                    marginBottom: '2rem',
                }}
            >
                <Typography variant="h6" sx={{ marginBottom: 8 }}>
                    {title}
                </Typography>
                <form>
                    <Stack
                        spacing={2}
                        direction="column"
                        sx={{ marginBottom: 4 }}
                    >
                        <TextField
                            type="text"
                            label="Collection Name"
                            onChange={(e) => setValue('name', e.target.value)}
                            value={values.name}
                            fullWidth
                            required
                        />
                        <MultiSelectAutocomplete
                            options={admins}
                            selectedOptions={values.orgCollectionAdminIds}
                            label="Superadmins"
                            placeholder="Select Superadmins"
                            allowSelectAll={false}
                            onSelectionChange={(newSelected) =>
                                setValue('orgCollectionAdminIds', newSelected)
                            }
                        />
                        <MultiSelectAutocomplete
                            options={orgs}
                            selectedOptions={values.orgIds}
                            label="Organizations"
                            placeholder="Select Organizations"
                            allowSelectAll={false}
                            onSelectionChange={(newSelected) =>
                                setValue('orgIds', newSelected)
                            }
                        />
                        <MultiSelectAutocomplete
                            options={admins}
                            selectedOptions={values.orgAdminIds}
                            label="Multi Campus Admins"
                            placeholder="Select Multi Campus Admins"
                            allowSelectAll={false}
                            onSelectionChange={(newSelected) =>
                                setValue('orgAdminIds', newSelected)
                            }
                        />
                    </Stack>
                    <LoadingButton
                        disabled={!formValid}
                        startIcon={<DomainAdd />}
                        size="small"
                        variant="contained"
                        onClick={handleSubmit}
                    >
                        Save
                    </LoadingButton>
                    {validationErrors && (
                        <ValidationErrors messages={validationErrors} />
                    )}
                </form>
            </Paper>
        </div>
    )
}

export default OrgCollectionForm
